import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useLocaleState } from "ra-core";
import { useSnapshot } from "valtio";
import viewerState, { isNiftiMetadata } from "stores/viewer";
import {
  isPetModality,
  petTracerLabel,
} from "lib/cornerstone/helpers/niftiDiscovery";

import { useLongitudinalStudies } from "shared/hooks/useLongitudinal";
import { YYYYMMDDtoDate } from "shared/utils/date";

type Props = {
  study_id: string;
  getViewerPath: (newStudyId?: string) => string | null;
};

function AcquisitionSelection({ study_id, getViewerPath }: Props) {
  const { patientStudies } = useLongitudinalStudies();
  const [currentLocal] = useLocaleState();
  const navigate = useNavigate();
  const { instanceMetadata, niftiActiveModality } = useSnapshot(viewerState);

  function acquisitionLabel(study) {
    const date = YYYYMMDDtoDate(study.StudyDate);
    const dateLabel =
      date && !Number.isNaN(date.getTime())
        ? new Intl.DateTimeFormat(currentLocal, {
            dateStyle: "long",
            timeZone: "UTC",
          }).format(date)
        : "Unknown date";
    const activeMetadata =
      study.StudyInstanceUID === study_id &&
      instanceMetadata?.StudyInstanceUID === study_id
        ? instanceMetadata
        : null;
    const modality =
      activeMetadata && isNiftiMetadata(activeMetadata)
        ? niftiActiveModality
        : activeMetadata?.Modality;
    const modalities =
      modality || study.ModalitiesInStudy || study.Modality || [];
    const modalityLabel = (
      Array.isArray(modalities) ? modalities : String(modalities).split("\\")
    )
      .filter((value) => value && !["SR", "SEG", "PR"].includes(value))
      .map((value) =>
        isPetModality(value)
          ? `PET ${petTracerLabel(value)}`
          : value === "PT"
          ? "PET"
          : value === "MR"
          ? "MRI"
          : value.toUpperCase(),
      )
      .filter((value, index, values) => values.indexOf(value) === index)
      .join(" / ");
    return modalityLabel ? `${dateLabel} · ${modalityLabel}` : dateLabel;
  }

  if (!patientStudies?.length) {
    return null;
  }

  function handleAcquisitionChange(e: SelectChangeEvent<string>) {
    const newStudyUID = e.target.value;
    const newPath = getViewerPath(newStudyUID);

    const selectedStudy = patientStudies?.find(
      (study) => study.StudyInstanceUID === newStudyUID,
    );

    if (newPath) {
      if (selectedStudy?.StudyDate) {
        navigate(`${newPath}?exam_date=${selectedStudy.StudyDate}`);
      } else {
        navigate(newPath);
      }
    }
  }

  return (
    <FormControl variant="filled">
      <InputLabel id="acquisition-selection-label"> Acquisition </InputLabel>
      <Select
        labelId="acquisition-selection-label"
        className="min-w-[8rem]"
        value={study_id || ""}
        name="Acquisition"
        size="small"
        onChange={handleAcquisitionChange}
      >
        {[...patientStudies]
          ?.sort((a, b) => a.StudyDate.localeCompare(b.StudyDate))
          ?.map((study) => {
            return (
              <MenuItem
                key={study.StudyInstanceUID}
                value={study.StudyInstanceUID}
              >
                {acquisitionLabel(study)}
              </MenuItem>
            );
          })}
      </Select>
    </FormControl>
  );
}

export default AcquisitionSelection;
